// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useState, type KeyboardEvent } from 'react';

import { useCanEdit } from '../../../app/CaseRoleContext';
import { lengthInput, parseLength, type EditField, type FieldKey, type PropertiesView } from '../../../bygning/geometriEdit';
import { CadIcon } from './cadIcons';
import styles from './PropertiesPanel.module.css';

export interface PropertiesPanelProps {
  view: PropertiesView;
  /** More than one selected: the panel shows the primary, and says so. */
  more: number;
  onField: (key: FieldKey, value: number | string) => void;
  onReset: (key: FieldKey) => void;
  onRevert: () => void;
  onDelete: () => void;
  onSelect: (guid: string) => void;
  onZoom: (() => void) | null;
}

/** A length or text field: typed, committed on Enter or blur, Esc puts the value back. */
function TextValue({ field, canEdit, onCommit }: { field: EditField; canEdit: boolean; onCommit: (value: number | string) => void }) {
  const shown = field.type === 'length' ? (typeof field.value === 'number' ? lengthInput(field.value) : '') : String(field.value ?? '');
  const [text, setText] = useState(shown);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    setText(shown);
    setBad(false);
  }, [shown]);
  const commit = () => {
    if (text === shown) return;
    if (field.type === 'length') {
      const m = parseLength(text);
      if (m === null) {
        setBad(true);
        return;
      }
      onCommit(m);
    } else onCommit(text);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setText(shown);
      setBad(false);
    }
  };
  return (
    <span className={styles.inputWrap}>
      <input
        className={`${styles.input}${field.type === 'length' ? ` ${styles.number}` : ''}${field.edited ? ` ${styles.edited}` : ''}${bad ? ` ${styles.bad}` : ''}`}
        value={text}
        inputMode={field.type === 'length' ? 'decimal' : 'text'}
        aria-label={field.label}
        aria-invalid={bad || undefined}
        readOnly={!canEdit || !field.editable}
        placeholder={field.type === 'length' ? 'ukendt' : undefined}
        onChange={(e) => {
          setText(e.target.value);
          setBad(false);
        }}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
      {field.type === 'length' && <span className={styles.unit}>m</span>}
    </span>
  );
}

function Field({ field, canEdit, onField, onReset }: { field: EditField; canEdit: boolean; onField: PropertiesPanelProps['onField']; onReset: PropertiesPanelProps['onReset'] }) {
  const id = `prop-${field.key}`;
  let value;
  if (!field.editable || (!canEdit && field.type !== 'choice')) {
    value = <span className={styles.readonly}>{field.display}</span>;
  } else if (field.type === 'choice') {
    value = (
      <span className={`${styles.choice}${field.edited ? ` ${styles.edited}` : ''}`} role="radiogroup" aria-labelledby={id}>
        {field.options!.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={o.value === field.value}
            disabled={!canEdit}
            className={o.value === field.value ? `${styles.option} ${styles.optionOn}` : styles.option}
            onClick={() => onField(field.key, o.value)}
          >
            {o.label}
          </button>
        ))}
      </span>
    );
  } else {
    value = <TextValue field={field} canEdit={canEdit} onCommit={(v) => onField(field.key, v)} />;
  }
  return (
    <div className={styles.field}>
      <span id={id} className={styles.label}>
        {field.label}
      </span>
      {value}
      {field.edited && field.detected !== null && canEdit ? (
        <button
          type="button"
          className={styles.reset}
          title={`Tilbage til detekteret: ${field.detected}`}
          aria-label={`${field.label} tilbage til detekteret (${field.detected})`}
          onClick={() => onReset(field.key)}
        >
          <CadIcon name="reset" className={styles.resetIcon} />
        </button>
      ) : (
        <span />
      )}
    </div>
  );
}

/**
 * Egenskaber, the floating properties panel (Spacio / Revit), right, only
 * while something is selected: kind and name, provenance chips, short
 * sections of fields. A field edited away from what detection made has an
 * accent border and ↺, which puts that one field back (its tooltip shows
 * the detected value). Footer: Tilbage til detekteret, Slet.
 */
export function PropertiesPanel({ view, more, onField, onReset, onRevert, onDelete, onSelect, onZoom }: PropertiesPanelProps) {
  const canEdit = useCanEdit();
  return (
    <section className={styles.panel} aria-labelledby="geo-props-title">
      <div className={styles.head}>
        <p className={styles.eyebrow}>{view.eyebrow}</p>
        {onZoom && (
          <button type="button" className={styles.zoom} onClick={onZoom}>
            Zoom til
          </button>
        )}
      </div>
      <h2 id="geo-props-title" className={styles.title}>
        {view.title}
      </h2>
      {more > 0 && <p className={styles.more}>+ {more} mere valgt</p>}
      <ul className={styles.chips} aria-label="Status">
        {view.chips.map((c) => (
          <li key={c.label} className={`${styles.chip} ${styles[c.tone]}`}>
            {c.label}
          </li>
        ))}
      </ul>
      {view.sections.map((s) => (
        <div key={s.title} className={styles.section}>
          <h3 className={styles.sectionTitle}>{s.title}</h3>
          {s.fields.map((f) => (
            <Field key={f.key} field={f} canEdit={canEdit} onField={onField} onReset={onReset} />
          ))}
        </div>
      ))}
      {view.related.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{view.kind === 'opening' ? 'Vært' : 'Åbninger'}</h3>
          <p className={styles.related}>
            {view.related.map((r) => (
              <button key={r.guid} type="button" className={styles.link} onClick={() => onSelect(r.guid)}>
                {r.label}
              </button>
            ))}
          </p>
        </div>
      )}
      {canEdit && (view.revertable || view.deletable) && (
        <div className={styles.footer}>
          {view.revertable && (
            <button type="button" className={styles.button} onClick={onRevert}>
              Tilbage til detekteret
            </button>
          )}
          {view.deletable && (
            <button type="button" className={styles.ghost} onClick={onDelete}>
              Slet
            </button>
          )}
        </div>
      )}
    </section>
  );
}
